import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorState } from '@shared/components/feedback/ErrorState'
import { LoadingState } from '@shared/components/feedback/LoadingState'
import { UnsavedChanges } from '@shared/components/form/UnsavedChanges'
import { PageHeader } from '@shared/components/layout/PageHeader'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs'
import { toast } from '@shared/hooks/useToast'
import { extractApiErrorMessage } from '@shared/lib/feedback/mutation-feedback'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { HexColorField } from '../../components/HexColorField'
import { PalettePreview } from '../../components/PalettePreview'
import {
  useCurrentTenantBranding,
  useDeleteTenantLogo,
  useUpdateTenantBranding,
  useUploadTenantLogo,
} from '../../hooks/useTenantBranding'
import { useTenantBrandingContext } from '../../hooks/useTenantBrandingContext'
import { readableTextFor } from '../../lib/colors'
import { checkPalette, fixPaletteContrast, formatRatio, MIN_TEXT_CONTRAST } from '../../lib/palette'
import type { BrandingColors, UpdateTenantBrandingDto } from '../../types/branding.types'

const colorTokenFields: Array<{ key: keyof BrandingColors; label: string }> = [
  { key: 'background', label: 'Fundo da página' },
  { key: 'foreground', label: 'Texto da página' },
  { key: 'card', label: 'Fundo dos cartões' },
  { key: 'cardForeground', label: 'Texto dos cartões' },
  { key: 'popover', label: 'Fundo dos menus' },
  { key: 'popoverForeground', label: 'Texto dos menus' },
  { key: 'primary', label: 'Cor principal' },
  { key: 'primaryForeground', label: 'Texto sobre a cor principal' },
  { key: 'secondary', label: 'Cor secundária' },
  { key: 'secondaryForeground', label: 'Texto sobre a cor secundária' },
  { key: 'muted', label: 'Fundo suave' },
  { key: 'mutedForeground', label: 'Texto secundário' },
  { key: 'accent', label: 'Destaque' },
  { key: 'accentForeground', label: 'Texto sobre destaque' },
  { key: 'destructive', label: 'Ações destrutivas' },
  { key: 'destructiveForeground', label: 'Texto das ações destrutivas' },
  { key: 'border', label: 'Bordas' },
  { key: 'input', label: 'Campos de entrada' },
  { key: 'ring', label: 'Foco do teclado' },
]

// O formato interno ("H S% L%") vem sempre dos seletores de cor; a pessoa nunca o digita.
const hslColor = z.string().regex(/^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/, 'Cor inválida')

const colorTokenSchema = z.object({
  background: hslColor,
  foreground: hslColor,
  card: hslColor,
  cardForeground: hslColor,
  popover: hslColor,
  popoverForeground: hslColor,
  primary: hslColor,
  primaryForeground: hslColor,
  secondary: hslColor,
  secondaryForeground: hslColor,
  muted: hslColor,
  mutedForeground: hslColor,
  accent: hslColor,
  accentForeground: hslColor,
  destructive: hslColor,
  destructiveForeground: hslColor,
  border: hslColor,
  input: hslColor,
  ring: hslColor,
})

const brandingSchema = z.object({
  displayName: z
    .string()
    .min(1, 'Informe um nome para exibição')
    .max(200, 'Máximo de 200 caracteres'),
  storageRootDirectory: z
    .string()
    .min(1, 'Informe o diretório raiz no R2')
    .max(120, 'Máximo de 120 caracteres')
    .regex(
      /^[a-z0-9][a-z0-9._-]*$/,
      'Use apenas letras minúsculas, números, ponto, hífen ou underscore',
    ),
  logoUrl: z.union([z.string().url('URL do logo inválida'), z.literal('')]),
  light: colorTokenSchema,
  dark: colorTokenSchema,
})

type BrandingFormData = z.infer<typeof brandingSchema>

function PaletteEditor({
  palette,
  title,
  displayName,
  values,
  onChange,
}: {
  palette: 'light' | 'dark'
  title: string
  displayName: string
  values: BrandingColors
  onChange: (patch: Partial<BrandingColors>) => void
}) {
  const checks = checkPalette(values)
  const failing = checks.filter((check) => !check.ok)
  const fixes = fixPaletteContrast(values)
  const fixable = Object.keys(fixes).length > 0
  // Trocar o texto não resolve quando o próprio fundo (ou a cor principal usada como texto) é o problema.
  const unresolved = checkPalette({ ...values, ...fixes }).filter((check) => !check.ok)

  return (
    <div className="space-y-8">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <HexColorField
            id={`${palette}-primary`}
            label="Cor principal"
            description="Botões, links e foco do teclado. A cor do texto sobre ela é escolhida sozinha."
            value={values.primary}
            onChange={(value) =>
              onChange({ primary: value, ring: value, primaryForeground: readableTextFor(value) })
            }
          />
          <HexColorField
            id={`${palette}-accent`}
            label="Destaque"
            description="Fundo de avisos que pedem atenção."
            value={values.accent}
            onChange={(value) =>
              onChange({ accent: value, accentForeground: readableTextFor(value) })
            }
          />
          <HexColorField
            id={`${palette}-background`}
            label="Fundo da página"
            value={values.background}
            onChange={(value) => onChange({ background: value })}
          />
          <HexColorField
            id={`${palette}-foreground`}
            label="Texto da página"
            value={values.foreground}
            onChange={(value) => onChange({ foreground: value })}
          />
        </div>
        <PalettePreview title={title} colors={values} displayName={displayName} />
      </div>

      <section aria-label={`Legibilidade: ${title}`} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Legibilidade das cores</h3>
            <p className="text-sm text-muted-foreground">
              Textos precisam de contraste mínimo de {formatRatio(MIN_TEXT_CONTRAST)} para serem
              lidos com conforto. Isto é um aviso: você ainda pode salvar.
            </p>
          </div>
          {failing.length > 0 && fixable ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange(fixPaletteContrast(values))}
            >
              Corrigir textos automaticamente
            </Button>
          ) : null}
        </div>
        {unresolved.length > 0 ? (
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Para {unresolved.map((check) => check.label.toLowerCase()).join('; ')}, trocar o texto
            não basta: escolha uma cor de fundo mais clara ou mais escura.
          </p>
        ) : null}
        <ul className="space-y-2">
          {checks.map((check) => (
            <li key={check.id} className="flex items-start gap-2 text-sm">
              {check.ok ? (
                <CheckCircle2
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                />
              ) : (
                <AlertTriangle
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
                />
              )}
              <span>
                {check.label}: <strong>{formatRatio(check.ratio)}</strong>{' '}
                {check.ok ? '(boa leitura)' : '(difícil de ler)'}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <details className="rounded-xl border p-4">
        <summary className="cursor-pointer text-sm font-medium">
          Ajustar todas as cores (avançado)
        </summary>
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {colorTokenFields.map((field) => (
            <HexColorField
              key={`${palette}-${field.key}`}
              id={`${palette}-all-${field.key}`}
              label={field.label}
              value={values[field.key]}
              onChange={(value) => onChange({ [field.key]: value })}
            />
          ))}
        </div>
      </details>
    </div>
  )
}

export function TenantBranding() {
  const { branding: activeBranding } = useTenantBrandingContext()
  const { data, isLoading, isError, refetch } = useCurrentTenantBranding()
  const updateMutation = useUpdateTenantBranding()
  const uploadMutation = useUploadTenantLogo()
  const deleteLogoMutation = useDeleteTenantLogo()
  const [uploadedLogos, setUploadedLogos] = useState<string[]>([])
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<BrandingFormData>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      displayName: activeBranding.branding.displayName,
      storageRootDirectory: activeBranding.branding.storageRootDirectory,
      logoUrl: activeBranding.branding.logoUrl ?? '',
      light: activeBranding.branding.light,
      dark: activeBranding.branding.dark,
    },
  })

  useEffect(() => {
    if (!data) {
      return
    }

    reset({
      displayName: data.branding.displayName,
      storageRootDirectory: data.branding.storageRootDirectory,
      logoUrl: data.branding.logoUrl ?? '',
      light: data.branding.light,
      dark: data.branding.dark,
    })

    setCurrentLogoUrl(data.branding.logoUrl ?? null)
  }, [data, reset])

  const watchedLight = watch('light')
  const watchedDark = watch('dark')
  const watchedLogoUrl = watch('logoUrl')
  const watchedDisplayName = watch('displayName')

  const applyPatch = (palette: 'light' | 'dark', patch: Partial<BrandingColors>) => {
    for (const [key, value] of Object.entries(patch)) {
      setValue(`${palette}.${key as keyof BrandingColors}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const handleLogoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    try {
      const uploaded = await uploadMutation.mutateAsync(file)
      setUploadedLogos((logos) => [...logos, uploaded.logoUrl])
      setValue('logoUrl', uploaded.logoUrl, { shouldDirty: true })
    } catch {
      /* O hook apresenta o erro de upload. */
    }
  }

  const handleRemoveLogo = async () => {
    setValue('logoUrl', '', { shouldDirty: true })
  }

  const onSubmit = async (values: BrandingFormData) => {
    const payload: UpdateTenantBrandingDto = {
      displayName: values.displayName.trim(),
      storageRootDirectory: values.storageRootDirectory.trim(),
      logoUrl: values.logoUrl ? values.logoUrl : null,
      light: values.light,
      dark: values.dark,
    }

    try {
      const response = await updateMutation.mutateAsync(payload)
      const retainedLogo = response.data?.branding.logoUrl ?? null
      const obsolete = [
        ...new Set([...uploadedLogos, ...(currentLogoUrl ? [currentLogoUrl] : [])]),
      ].filter((url) => url !== retainedLogo)
      setCurrentLogoUrl(retainedLogo)
      setUploadedLogos([])
      for (const url of obsolete) {
        try {
          await deleteLogoMutation.mutateAsync(url)
        } catch {
          toast({
            title: 'Identidade visual salva',
            description: 'A limpeza de um arquivo antigo ficou pendente.',
            variant: 'warning',
          })
        }
      }
    } catch {
      /* O hook apresenta o erro de atualização. */
    }
  }

  if (isLoading) {
    return <LoadingState message="Carregando identidade visual..." />
  }

  if (isError || !data) {
    return (
      <ErrorState
        message="Não foi possível carregar a configuração de branding deste tenant."
        retry={() => refetch()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Identidade visual"
        eyebrow="Configurações · Identidade"
        description="Escolha o nome, o logo e as cores da sua organização. Cada tema (claro e escuro) mostra uma prévia e avisa se algum texto ficar difícil de ler."
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <UnsavedChanges
          dirty={isDirty}
          saving={
            updateMutation.isPending || uploadMutation.isPending || deleteLogoMutation.isPending
          }
        />
        <Card className="rounded-[1.75rem] border-border/70 bg-card/95 p-6 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_320px]">
            <div className="space-y-4">
              <div>
                <Label htmlFor="displayName">Nome exibido</Label>
                <Input id="displayName" {...register('displayName')} className="mt-2" />
                {errors.displayName ? (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                    {errors.displayName.message}
                  </p>
                ) : null}
              </div>

              <details className="rounded-xl border p-3">
                <summary>Configuração técnica de armazenamento</summary>
                <div>
                  <Label htmlFor="storageRootDirectory">
                    Diretório gerenciado pela infraestrutura
                  </Label>
                  <Input
                    id="storageRootDirectory"
                    readOnly
                    {...register('storageRootDirectory')}
                    className="mt-2"
                  />
                  {errors.storageRootDirectory ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                      {errors.storageRootDirectory.message}
                    </p>
                  ) : null}
                </div>
              </details>

              <div className="rounded-2xl border border-dashed border-border/70 bg-background/50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium">Upload do logo</p>
                    <p className="text-xs text-muted-foreground">
                      Selecione a imagem da organização. A identidade atual só muda ao salvar.
                    </p>
                  </div>
                  <Input
                    type="file"
                    aria-label="Selecionar imagem para o logo"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(event) => void handleLogoSelected(event)}
                    className="max-w-xs"
                  />
                </div>
              </div>

              <details className="rounded-xl border p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  Usar o endereço de uma imagem em vez de enviar um arquivo
                </summary>
                <div className="mt-3">
                  <Label htmlFor="logoUrl">URL do logo</Label>
                  <Input id="logoUrl" {...register('logoUrl')} className="mt-2" />
                  {errors.logoUrl ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                      {errors.logoUrl.message}
                    </p>
                  ) : null}
                </div>
              </details>
            </div>

            <div className="rounded-[1.5rem] border border-border/70 bg-background/60 p-5">
              <p className="text-sm font-medium">Prévia da identidade</p>
              <div className="mt-4 flex items-center gap-4">
                {watchedLogoUrl ? (
                  <img
                    src={watchedLogoUrl}
                    alt="Logo do tenant"
                    className="h-16 w-16 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-xl font-bold text-primary-foreground">
                    {watch('displayName').slice(0, 1).toUpperCase() || 'V'}
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold">{watch('displayName')}</p>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    comunicação interna
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <Button type="button" variant="outline" onClick={() => void handleRemoveLogo()}>
                  Remover logo
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-[1.75rem] border-border/70 bg-card/95 p-6 shadow-sm">
          <Tabs defaultValue="light">
            <TabsList className="rounded-2xl">
              <TabsTrigger value="light">Paleta clara</TabsTrigger>
              <TabsTrigger value="dark">Paleta escura</TabsTrigger>
            </TabsList>
            <TabsContent value="light" className="mt-6">
              <PaletteEditor
                palette="light"
                title="Tema claro"
                displayName={watchedDisplayName}
                values={watchedLight}
                onChange={(patch) => applyPatch('light', patch)}
              />
            </TabsContent>
            <TabsContent value="dark" className="mt-6">
              <PaletteEditor
                palette="dark"
                title="Tema escuro"
                displayName={watchedDisplayName}
                values={watchedDark}
                onChange={(patch) => applyPatch('dark', patch)}
              />
            </TabsContent>
          </Tabs>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              try {
                for (const url of uploadedLogos) await deleteLogoMutation.mutateAsync(url)
                setUploadedLogos([])
                reset()
              } catch (error) {
                toast({
                  title: 'Não foi possível limpar o upload descartado',
                  description: extractApiErrorMessage(error, 'Tente novamente.'),
                  variant: 'destructive',
                })
              }
            }}
          >
            Descartar
          </Button>
          <Button
            type="submit"
            disabled={
              !isDirty ||
              updateMutation.isPending ||
              uploadMutation.isPending ||
              deleteLogoMutation.isPending
            }
          >
            {updateMutation.isPending ? 'Salvando...' : 'Salvar identidade visual'}
          </Button>
        </div>
      </form>
    </div>
  )
}
